import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Loader2, FileImage } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

/**
 * Drag-and-drop upload zone with progress tracking
 * 
 * Props:
 * - onUpload: async (files: File[]) => Promise<void> - called when files are dropped/selected
 * - accept: string - file types to accept (e.g., "image/*")
 * - multiple: boolean - allow multiple files
 * - maxSize: number - max file size in bytes (default 50MB)
 * - disabled: boolean - disable uploads
 * - className: string - additional classes
 */
export default function DropZone({
  onUpload,
  accept = "image/*",
  multiple = true,
  maxSize = 50 * 1024 * 1024, // 50MB
  disabled = false,
  className = "",
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const fileInputRef = useRef(null);

  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Process files
  const processFiles = useCallback(async (files) => {
    if (disabled) return;

    const validFiles = Array.from(files).filter(file => {
      // Check size
      if (file.size > maxSize) {
        console.warn(`File ${file.name} exceeds max size of ${maxSize} bytes`);
        return false;
      }
      // Check type
      if (accept && accept !== "*") {
        const acceptTypes = accept.split(',').map(t => t.trim());
        const fileType = file.type;
        const fileExt = '.' + file.name.split('.').pop().toLowerCase();
        const isAccepted = acceptTypes.some(type => {
          if (type.endsWith('/*')) {
            return fileType.startsWith(type.replace('/*', '/'));
          }
          return type === fileType || type === fileExt;
        });
        if (!isAccepted) {
          console.warn(`File ${file.name} type ${fileType} not accepted`);
          return false;
        }
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Add to queue
    const queueItems = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      status: 'pending', // pending, uploading, done, error
      progress: 0,
      error: null,
    }));

    setUploadQueue(prev => [...prev, ...queueItems]);

    // Process uploads
    if (onUpload) {
      for (const item of queueItems) {
        try {
          setUploadQueue(prev => prev.map(q =>
            q.id === item.id ? { ...q, status: 'uploading' } : q
          ));

          await onUpload([item.file], (progress) => {
            setUploadQueue(prev => prev.map(q =>
              q.id === item.id ? { ...q, progress } : q
            ));
          });

          setUploadQueue(prev => prev.map(q =>
            q.id === item.id ? { ...q, status: 'done', progress: 100 } : q
          ));

          // Remove completed after delay
          setTimeout(() => {
            setUploadQueue(prev => prev.filter(q => q.id !== item.id));
          }, 2000);

        } catch (err) {
          setUploadQueue(prev => prev.map(q =>
            q.id === item.id ? { ...q, status: 'error', error: err.message } : q
          ));
        }
      }
    }
  }, [disabled, maxSize, accept, onUpload]);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFiles(multiple ? files : [files[0]]);
    }
  }, [processFiles, multiple]);

  // Handle click to browse
  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove from queue
  const removeFromQueue = (id) => {
    setUploadQueue(prev => prev.filter(q => q.id !== id));
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className={className}>
      {/* Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          transition-all duration-200
          ${disabled
            ? 'border-white/10 bg-white/5 cursor-not-allowed opacity-50'
            : isDragging
              ? 'border-[#C6A14A] bg-[#C6A14A]/10 scale-[1.02]'
              : 'border-white/20 bg-[#050816]/30 hover:border-[#C6A14A]/50 hover:bg-[#050816]/50'
          }
        `}
        data-testid="dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
          data-testid="dropzone-input"
        />
        
        <div className={`
          mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4
          ${isDragging ? 'bg-[#C6A14A]/20' : 'bg-white/5'}
        `}>
          <Upload className={`h-6 w-6 ${isDragging ? 'text-[#C6A14A]' : 'text-[#E8E4DE]/60'}`} />
        </div>
        
        <p className={`text-sm font-medium ${isDragging ? 'text-[#C6A14A]' : 'text-white'}`}>
          {isDragging ? 'Drop files here' : 'Drag & drop files here'}
        </p>
        <p className="text-xs text-[#E8E4DE]/50 mt-1">
          or click to browse
        </p>
        <p className="text-xs text-[#E8E4DE]/30 mt-2">
          Max {formatSize(maxSize)} per file
        </p>
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className="mt-4 space-y-2">
          {uploadQueue.map((item) => (
            <div
              key={item.id}
              className={`
                flex items-center gap-3 p-3 rounded-lg
                ${item.status === 'error' ? 'bg-red-500/10 border border-red-500/30' :
                  item.status === 'done' ? 'bg-green-500/10 border border-green-500/30' :
                  'bg-[#050816]/50 border border-white/10'}
              `}
              data-testid={`upload-item-${item.id}`}
            >
              {/* Icon */}
              <div className="shrink-0">
                {item.status === 'uploading' ? (
                  <Loader2 className="h-5 w-5 text-[#C6A14A] animate-spin" />
                ) : item.status === 'done' ? (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                ) : item.status === 'error' ? (
                  <AlertCircle className="h-5 w-5 text-red-400" />
                ) : (
                  <FileImage className="h-5 w-5 text-[#E8E4DE]/60" />
                )}
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{item.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  {item.status === 'uploading' && (
                    <>
                      <Progress value={item.progress} className="h-1.5 flex-1" />
                      <span className="text-xs text-[#E8E4DE]/60">{item.progress}%</span>
                    </>
                  )}
                  {item.status === 'pending' && (
                    <span className="text-xs text-[#E8E4DE]/50">Waiting...</span>
                  )}
                  {item.status === 'done' && (
                    <span className="text-xs text-green-400">Uploaded</span>
                  )}
                  {item.status === 'error' && (
                    <span className="text-xs text-red-400">{item.error || 'Upload failed'}</span>
                  )}
                </div>
              </div>

              {/* Size */}
              <span className="text-xs text-[#E8E4DE]/40 shrink-0">
                {formatSize(item.size)}
              </span>

              {/* Remove button */}
              {(item.status === 'pending' || item.status === 'error') && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromQueue(item.id);
                  }}
                  className="shrink-0 text-[#E8E4DE]/40 hover:text-red-400 hover:bg-transparent p-1 h-auto"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
